import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import DataTable, { DataTableColumn } from '../../../ui/DataTable';
import RowActionMenu, { RowActionMenuItem } from '../../../ui/RowActionMenu';
import { EmailTemplate } from '../../../../types/email';
import { handleError } from '../../../../utils/errorHandler';
import TemplateDetailsModal from '../../../modal/TemplateDetailsModal';
import templateApi from '../../../../repositories/template';
import { useAppSelector } from '../../../../hooks/redux';
import { fromBase64 } from '../../../../utils/base64';
import SettingsPanel from '../../../ui/SettingsPanel';
import Button from '../../../ui/Button';
import { swalColors } from '../../../../utils/swal';

interface TemplateDetails {
  id: string | number;
  name: string;
  content: string;
  isGlobal: boolean;
}

const Templates: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.role === 'admin';
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);
  const [selectedTemplateDetails, setSelectedTemplateDetails] = useState<TemplateDetails | null>(null);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await templateApi.getTemplates();
      if (response?.data?.success) {
        // Sort templates: default first, then by name
        const sortedTemplates = [...(response.data.templates || [])].sort((a, b) => {
          if (a.isDefault && !b.isDefault) return -1;
          if (!a.isDefault && b.isDefault) return 1;
          return a.name.localeCompare(b.name);
        });
        setTemplates(sortedTemplates);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleCreateTemplate = () => {
    navigate('/email/template/create');
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    if (template.isGlobal && !isAdmin) {
      Swal.fire({
        title: 'Cannot Edit',
        text: 'Global templates cannot be edited. You can create your own template instead.',
        icon: 'info',
        confirmButtonText: 'OK',
      });
      return;
    }
    navigate(`/email/template/edit/${template.id}`);
  };

  const handleDeleteTemplate = async (id: number) => {
    const template = templates.find((t) => t.id === id);

    if (template?.isGlobal && !isAdmin) {
      Swal.fire({
        title: 'Cannot Delete',
        text: 'Global templates cannot be deleted.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete the template "${template?.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalColors.danger,
      confirmButtonText: 'Yes, delete',
    });

    if (result.isConfirmed) {
      try {
        const response = await templateApi.deleteTemplate(id);
        if (response?.data?.success) {
          await fetchTemplates();
          Swal.fire({
            title: 'Deleted!',
            text: 'Template has been deleted.',
            icon: 'success',
            confirmButtonText: 'OK',
          });
        }
      } catch (error) {
        handleError(error);
      }
    }
  };

  // set-default toggles: called on the current default it clears the flag,
  // otherwise it sets it. The response carries the resulting isDefault and a
  // matching message, so one handler covers both directions.
  const handleToggleDefault = async (id: number) => {
    try {
      const response = await templateApi.setDefaultTemplate(id);
      if (response?.data?.success) {
        await fetchTemplates();
        Swal.fire({
          title: 'Success!',
          text: response.data.message ?? 'Default template updated.',
          icon: 'success',
          confirmButtonText: 'OK',
        });
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleTemplateClick = async (templateId: number) => {
    try {
      const response = await templateApi.getTemplate(templateId);
      if (!response?.data?.success) return;
      const t = response.data.template;
      setSelectedTemplateDetails({
        id: t.id,
        name: t.name,
        content: fromBase64(t.greetingText) + '\n\n'+ fromBase64(t.headerText) + '\n\n' + fromBase64(t.pitchData) + '\n\n' + fromBase64(t.closingText),
        isGlobal: t.isGlobal,
      });
      setIsDetailsModalOpen(true);
    } catch (error) {
      handleError(error);
    }
  };

  const templateColumns: DataTableColumn<any>[] = [
    {
      key: "name",
      header: "Template Name",
      cell: (template) => (
        <>
          <button
            onClick={() => handleTemplateClick(template.id)}
            className="border border-transparent text-sm font-medium text-primary-700 hover:text-primary-900 hover:underline focus:outline-none rounded focus-visible:border-primary-700"
          >
            {template.name}
          </button>
          {template.isDefault ? <span className='text-neutral-400'> (Default)</span> : ""}
        </>
      ),
    },
    {
      key: "type",
      header: "Type",
      // Category tag, not a status - neutral, with Global distinguished only
      // because it is a meaningful ownership distinction.
      cell: (template) =>
        template.isGlobal ? (
          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium border rounded-full bg-primary-50 text-primary-800 border-primary-200">
            Global
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium border rounded-full bg-neutral-100 text-neutral-700 border-neutral-200">
            Custom
          </span>
        ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      cell: (template) => {
        const items: RowActionMenuItem[] = [];
        if (!template.isGlobal || isAdmin) {
          items.push({ label: "Edit", onClick: () => handleEditTemplate(template) });
        }
        // Default is a per-user choice, so admins (who manage the global set)
        // never see it. Whichever way the template is currently set, the menu
        // offers the opposite action - without the "Remove as Default" branch a
        // global template that is already the default had no items at all, and
        // the menu opened as an empty sliver.
        if (!isAdmin) {
          items.push(
            template.isDefault
              ? {
                  label: "Remove as Default",
                  icon: <StarIconSolid className="w-4 h-4 text-warning-500" />,
                  onClick: () => handleToggleDefault(template.id),
                }
              : {
                  label: "Set as Default",
                  icon: <StarIcon className="w-4 h-4" />,
                  onClick: () => handleToggleDefault(template.id),
                }
          );
        }
        if (!template.isGlobal || isAdmin) {
          items.push({ label: "Delete", danger: true, onClick: () => handleDeleteTemplate(template.id) });
        }
        return <RowActionMenu items={items} ariaLabel="Template actions" />;
      },
    },
  ];

  return (
    <SettingsPanel
      title="Email Templates"
      description="Manage your email templates"
      actions={
        <Button
          onClick={handleCreateTemplate}
          className="shrink-0"
        >
          Create Template
        </Button>
      }
    >

      {/* Template Table */}
      <DataTable
        columns={templateColumns}
        rows={templates}
        getRowKey={(t: any) => t.id}
        loading={loading}
        emptyMessage="No templates found. Create your first template to get started."
      />

      {/* Template Details Modal */}
      <TemplateDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        template={selectedTemplateDetails}
      />
    </SettingsPanel>
  );
};

export default Templates;
